import { useRef, useState } from 'react';
import { Loading, Table } from 'react-daisyui';
import { useTranslation } from 'react-i18next';
import { useAdvanceSearchModalContext } from '../AdvanceSearchModal.hook';
import '../AdvanceSearchModal.styles.scss';
import TableRow from './TableRow.component';

export default function LeadsTable() {
  const {
    leads,
    loading,
    isLastPage,
    inputValue,
    stateFilters,
    assignedUserFilters,
    funnelForFilters,
    stageForFilters,
    dateRange,
    getMoreLeads,
  } = useAdvanceSearchModalContext();
  const { t } = useTranslation('navbar/navbar');

  const [loadingMoreLeads, setLoadingMoreLeads] = useState(false);
  const observer = useRef<IntersectionObserver | null>(null);

  function lastLeadRef(lead: HTMLDivElement) {
    if (loadingMoreLeads) return;

    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting && !isLastPage) {
        setLoadingMoreLeads(true);

        await getMoreLeads();

        setLoadingMoreLeads(false);
      }
    });

    if (lead) observer.current.observe(lead);
  }

  const [startDate, endDate] = dateRange;
  const showEmptyState =
    !leads.length &&
    !inputValue.length &&
    !stateFilters &&
    !assignedUserFilters &&
    !funnelForFilters &&
    !stageForFilters &&
    !startDate &&
    !endDate;
  const showFailureState = !leads.length && !loading;

  return (
    (loading && (
      <div className="AdvanceSearchModal__Body__Loading">
        <Loading size="lg" color="primary" />
      </div>
    )) ||
    (showEmptyState && (
      <div className="AdvanceSearchModal__Body__Container">
        <img src="/images/navbar/advanceSearch.svg" className="mt-6" />
        <p className="AdvanceSearchModal__Body__Container__EmptyDescription">
          {t('advanceSearch.emptyDescription')}
        </p>
      </div>
    )) ||
    (showFailureState && (
      <div className="AdvanceSearchModal__Body__Container">
        <img src="/images/navbar/sadEmoji.svg" className="mt-16" />
        <h3 className="AdvanceSearchModal__Body__Container__NoResultsTitle">
          {t('advanceSearch.noResultsTitle')}
        </h3>
        <p className="AdvanceSearchModal__Body__Container__NoResultsDescription">
          {t('advanceSearch.noResultsDescription')}
        </p>
      </div>
    )) || (
      <div className="AdvanceSearchModal__Body__Table">
        <Table pinRows zebra>
          <Table.Head className="AdvanceSearchModal__Body__Table__Head">
            <span />
            <span className="AdvanceSearchModal__Body__Table__Head__Title">
              {t('advanceSearch.tableHeadTitle')}
            </span>
            <span className="AdvanceSearchModal__Body__Table__Head__Title">
              {t('advanceSearch.tableHeadFunnel')}
            </span>
            <span className="AdvanceSearchModal__Body__Table__Head__Title">
              {t('advanceSearch.tableHeadStage')}
            </span>
            <span className="AdvanceSearchModal__Body__Table__Head__Title">
              {t('advanceSearch.tableHeadUser')}
            </span>
          </Table.Head>
          <Table.Body>
            {leads.map((lead, index) => {
              if (leads.length === index + 1) {
                return (
                  <TableRow innerRef={lastLeadRef} key={lead.id} lead={lead} />
                );
              }

              return <TableRow key={lead.id} lead={lead} />;
            })}
            {loadingMoreLeads ? (
              <tr>
                <td colSpan={5} align="center">
                  <Loading color="primary" />
                </td>
              </tr>
            ) : null}
          </Table.Body>
        </Table>
      </div>
    )
  );
}
